import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import ConfirmModal from '../components/ConfirmModal';
import { twoOneOneApi } from '../services/api';
import { STORAGE_KEY } from './TwoOneOne';

const STEP_LABELS = ['1st Dose', '+24h', '+48h'];
const PILL_COUNTS = [2, 1, 1];
const getStepLabel = (i) => STEP_LABELS[i] ?? `+${i * 24}h`;
const getStepPills = (i) => PILL_COUNTS[i] ?? 1;

const STATUS_CONFIG = {
  confirmed: { badge: 'bg-green-50 text-green-700 border-green-200', dot: 'bg-green-500' },
  sent:      { badge: 'bg-blue-50 text-blue-700 border-blue-200',    dot: 'bg-blue-500'  },
  due:       { badge: 'bg-amber-50 text-amber-700 border-amber-200', dot: 'bg-gray-300'  },
};
const STATUS_LABELS = { confirmed: 'Done', sent: 'Sent', due: 'Due' };

const PillDots = ({ count }) => (
  <span className="inline-flex gap-0.5 items-center">
    {Array.from({ length: count }).map((_, i) => (
      <span key={i} className="w-2 h-1 rounded-full bg-blue-400 inline-block" />
    ))}
  </span>
);

const IconTrash = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

const CheckIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const sessionProgress = (reminders) => {
  const confirmed = reminders.filter(r => r.status === 'confirmed').length;
  return { confirmed, total: reminders.length };
};

const PrepHistory = () => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [reopenId, setReopenId] = useState(null);
  const [reopeningId, setReopeningId] = useState(null);
  const [expandedIds, setExpandedIds] = useState(new Set());

  const toggleExpanded = (id) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await twoOneOneApi.getSessions();
      setSessions(res.data);
    } catch {
      setError('Failed to load prep history.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async (id) => {
    setConfirmId(null);
    setDeletingId(id);
    try {
      await twoOneOneApi.deleteSession(id);
      setSessions(prev => prev.filter(s => s.id !== id));
    } catch {
      setError('Failed to delete session.');
    } finally {
      setDeletingId(null);
    }
  };

  // Reopen a closed session and drop straight into it on the EP page. The EP
  // page reads its session from localStorage first, so pointing that at the
  // reopened id is what makes it the current session, even when a newer one
  // is still open.
  const handleReopen = async (id) => {
    setReopenId(null);
    setReopeningId(id);
    setError('');
    try {
      await twoOneOneApi.reopenSession(id);
      try { localStorage.setItem(STORAGE_KEY, String(id)); } catch { /* storage unavailable */ }
      navigate('/ep');
    } catch {
      setError('Failed to reopen session.');
      setReopeningId(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {reopenId && (
        <ConfirmModal
          message="Reopen this session? Reminders that were never sent will be scheduled again."
          confirmLabel="Open session"
          variant="neutral"
          onConfirm={() => handleReopen(reopenId)}
          onCancel={() => setReopenId(null)}
        />
      )}
      {confirmId && (
        <ConfirmModal
          message="Delete this session and all its reminders?"
          onConfirm={() => handleDelete(confirmId)}
          onCancel={() => setConfirmId(null)}
        />
      )}
      <Navbar />
      <div className="max-w-lg mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900">History</h1>
          <Link to="/ep" className="text-sm font-medium px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:border-gray-300 hover:text-gray-800 transition bg-white shadow-sm">
            &larr; EP
          </Link>
        </div>

        {error && (
          <div
            className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 cursor-pointer text-sm"
            onClick={() => setError('')}
          >
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" />
          </div>
        ) : sessions.length === 0 ? (
          <div className="text-center py-12 text-gray-400 text-sm">No prep sessions yet.</div>
        ) : (
          <div className="flex flex-col gap-3">
            {sessions.map(session => {
              const { confirmed, total } = sessionProgress(session.reminders);
              const allDone = confirmed === total && total > 0;
              const isClosed = Boolean(session.closed_at);

              const isExpanded = expandedIds.has(session.id);

              return (
                <div
                  key={session.id}
                  className={`bg-white rounded-xl border shadow-sm overflow-hidden ${
                    allDone ? 'border-green-200' : 'border-gray-100'
                  }`}
                >
                  {/* Session header */}
                  <div
                    className={`flex items-center justify-between px-5 py-3 cursor-pointer select-none ${
                      allDone ? 'bg-green-50' : 'bg-gray-50'
                    }`}
                    onClick={() => toggleExpanded(session.id)}
                  >
                    <div className="flex items-center gap-2.5 flex-wrap">
                      <svg
                        className={`w-3.5 h-3.5 flex-shrink-0 transition-transform ${allDone ? 'text-green-600' : 'text-gray-400'} ${isExpanded ? 'rotate-90' : ''}`}
                        fill="none" stroke="currentColor" viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                      </svg>
                      <span className={`text-sm font-semibold ${allDone ? 'text-green-800' : 'text-gray-700'}`}>
                        {new Date(session.created_at).toLocaleDateString(undefined, {
                          month: 'short', day: 'numeric', year: 'numeric'
                        })}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium border ${
                        allDone
                          ? 'bg-green-100 text-green-700 border-green-200'
                          : 'bg-gray-100 text-gray-500 border-gray-200'
                      }`}>
                        {confirmed}/{total} done
                      </span>
                      {isClosed && (
                        <span className="text-xs px-2 py-0.5 rounded-full font-medium border bg-gray-100 text-gray-500 border-gray-200">
                          Closed
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      {isClosed && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setReopenId(session.id); }}
                          disabled={reopeningId === session.id}
                          className="text-xs font-medium px-2.5 py-1 rounded-md border border-gray-200 text-gray-600 hover:border-gray-300 hover:text-gray-800 transition bg-white disabled:opacity-50"
                          title="Reopen session"
                        >
                          {reopeningId === session.id ? 'Opening…' : 'Open'}
                        </button>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); setConfirmId(session.id); }}
                        disabled={deletingId === session.id}
                        className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition disabled:opacity-40"
                        title="Delete session"
                      >
                        <IconTrash />
                      </button>
                    </div>
                  </div>

                  {isExpanded && (
                    <>
                  {/* Note */}
                  {session.comment && (
                    <div className="px-5 py-2.5 border-t border-gray-50 bg-white">
                      <p className="text-xs text-gray-400 mb-0.5">Note</p>
                      <p className="text-xs text-gray-600 whitespace-pre-wrap">{session.comment}</p>
                    </div>
                  )}

                  {/* Reminder rows */}
                  <div className="divide-y divide-gray-50">
                    {session.reminders.map((r, i) => {
                      const cfg = STATUS_CONFIG[r.status] ?? STATUS_CONFIG.due;
                      const isConfirmed = r.status === 'confirmed';
                      return (
                        <div key={r.id} className="flex items-center gap-3 px-5 py-3">
                          {/* Step dot */}
                          <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                            isConfirmed ? 'bg-green-500' : 'bg-gray-200'
                          }`}>
                            {isConfirmed
                              ? <CheckIcon />
                              : <span className="text-xs text-gray-500 font-bold">{i + 1}</span>
                            }
                          </div>

                          {/* Label + pills */}
                          <div className="flex-1 min-w-0 flex items-center gap-2">
                            <span className="text-sm text-gray-700 font-medium w-16 flex-shrink-0">
                              {getStepLabel(i)}
                            </span>
                            <PillDots count={getStepPills(i)} />
                          </div>

                          {/* Status badge */}
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full border flex-shrink-0 ${cfg.badge}`}>
                            {STATUS_LABELS[r.status]}
                          </span>

                          {/* Confirmed time */}
                          {r.confirmed_at && (
                            <span className="text-xs text-gray-400 flex-shrink-0 hidden sm:block">
                              {new Date(r.confirmed_at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default PrepHistory;
